import React from "react";
import { Modal } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { ModalBox } from "./modal-box.component";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { UnsharpLogoInfo } from "../../../utils/functions/pdfSharpGate";

// The default brand green (WhiteLabelContext's default colorHex). The dialog
// is also shown on the PUBLIC proof screens, which have no WhiteLabelContext
// - so it cannot use DialogFrame (that reads the context) and takes the
// colour as an optional prop instead.
const DEFAULT_COLOR_HEX = "#7BBB58";

export interface PdfNotSharpDialogProps {
  open: boolean;
  logos: UnsharpLogoInfo[];
  colorHex?: string;
  onTryAgain: () => void;
  onDownloadAnyway: () => void;
  onCancel: () => void;
}

/**
 * Flag REACT_APP_PDF_WAIT_SHARP_ENABLED: shown when the proof PDF was STOPPED
 * because one or more logo images are not sharp yet (nothing was downloaded).
 * "Try again" = a new run with a new call budget, "Download anyway" = finish
 * the PDF with the images at hand (0 new calls), "Cancel" = nothing.
 */
export function PdfNotSharpDialog({
  open,
  logos,
  colorHex,
  onTryAgain,
  onDownloadAnyway,
  onCancel,
}: PdfNotSharpDialogProps) {
  const { t } = useTranslation();
  const color = colorHex || DEFAULT_COLOR_HEX;

  return (
    <Modal
      aria-labelledby="pdf-not-sharp-title"
      aria-describedby="pdf-not-sharp-body"
      open={open}
      onClose={onCancel}
    >
      <ModalBox
        // "!" on both: ModalBox's own w-fit / min-w-[400px] have the same
        // specificity and come LATER in the built CSS, so a plain w-[92vw]
        // loses to w-fit (the dialog then touched the screen edges at 375 px).
        className="relative !min-w-0 !w-[92vw] max-w-[480px]"
        onClick={(e) => e.stopPropagation()}
        data-testid="pdf-not-sharp-dialog"
        style={
          {
            "--primary": color,
            "--primary-bg": `${color}10`,
          } as React.CSSProperties
        }
      >
        <div className="flex flex-col space-y-4">
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon
              size="2x"
              icon={faTriangleExclamation}
              className="text-[#E8A33D]"
            />
            <h2 id="pdf-not-sharp-title" className="text-lg font-semibold">
              {t("pdfSharp.notSharpTitle")}
            </h2>
          </div>
          <p id="pdf-not-sharp-body" className="text-sm">
            {t("pdfSharp.notSharpBody")}
          </p>
          <ul className="text-sm list-disc pl-5 space-y-1 max-h-[30vh] overflow-y-auto">
            {logos.map((logo) => (
              <li key={logo.id}>
                <span className="font-semibold">{logo.title}</span>
                {logo.serial ? <span>{` (${logo.serial})`}</span> : null}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <SecondaryButton width="fit" className="px-4" onClick={onCancel}>
              {t("pdfSharp.cancel")}
            </SecondaryButton>
            <SecondaryButton
              width="fit"
              className="px-4"
              onClick={onDownloadAnyway}
            >
              {t("pdfSharp.downloadAnyway")}
            </SecondaryButton>
            <PrimaryButton width="fit" className="px-4" onClick={onTryAgain}>
              {t("pdfSharp.tryAgain")}
            </PrimaryButton>
          </div>
        </div>
      </ModalBox>
    </Modal>
  );
}
